import type { MapHitEvent, MapInteractionsConfig, MapSurface } from './map-interactions.js'

const LOG_PREFIX = '[gpx-map-interactions]'

let forcedDebug: boolean | null = null

/** Turn interaction tracing on/off from app code (e.g. maps-demo). */
export function setMapInteractionsDebug(enabled: boolean): void {
  forcedDebug = enabled
  if (enabled && typeof console !== 'undefined') {
    console.info(
      `${LOG_PREFIX} debug enabled. Disable: setMapInteractionsDebug(false) or localStorage.removeItem('gpx-map-interactions-debug')`,
    )
  }
}

export function isMapInteractionsDebug(): boolean {
  if (forcedDebug === true) return true
  if (forcedDebug === false) return false
  try {
    const g = globalThis as {
      __GPX_MAP_INTERACTIONS_DEBUG__?: boolean
    }
    if (g.__GPX_MAP_INTERACTIONS_DEBUG__ === true) return true
  } catch {
    /* ignore */
  }
  try {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem('gpx-map-interactions-debug') === '1'
    }
  } catch {
    /* ignore */
  }
  return false
}

export function summarizeHitTarget(event: MapHitEvent): Record<string, unknown> {
  const { trigger, latlng, target } = event
  const base = { trigger, lat: latlng.lat, lng: latlng.lng }
  if (target.type === 'marker') {
    return {
      ...base,
      targetType: 'marker',
      layerId: target.layerId,
      markerId: target.marker.id,
    }
  }
  if (target.type === 'feature') {
    const props = target.feature.properties as Record<string, unknown> | null
    return {
      ...base,
      targetType: 'feature',
      layerId: target.layerId,
      demoInteractionKind: props?.demoInteractionKind,
      featureId: props?.id ?? props?.demoId,
    }
  }
  if (target.type === 'pointCloud') {
    return {
      ...base,
      targetType: 'pointCloud',
      layerId: target.layerId,
      pointIndex: target.pointIndex,
      payloadKeys: target.point.payload ? Object.keys(target.point.payload) : [],
    }
  }
  return {
    ...base,
    targetType: 'cluster',
    layerId: target.layerId,
    pointCount: target.cluster.pointCount,
    childCount: target.cluster.childMarkerIds?.length,
  }
}

export function summarizeSurface(surface: MapSurface | null): Record<string, unknown> | null {
  if (!surface) return null
  const content =
    surface.content.format === 'text'
      ? { format: 'text', preview: surface.content.value.slice(0, 80) }
      : { format: 'html', preview: surface.content.value.slice(0, 80) }
  return { presentation: surface.presentation, content }
}

export function summarizeInteractionsConfig(
  config: MapInteractionsConfig | null,
): Record<string, unknown> {
  if (!config) return { active: false }
  return {
    active: true,
    triggers: config.triggers ?? ['click', 'hover'],
    hasSurface: Boolean(config.surface),
    clusterOnClick: config.cluster?.onClick ?? 'expand',
    hover: config.hover ?? null,
  }
}

export function logMapInteractions(
  provider: string,
  phase: string,
  detail: Record<string, unknown> = {},
): void {
  if (!isMapInteractionsDebug()) return
  console.log(`${LOG_PREFIX} ${provider} · ${phase}`, detail)
}
